/* ==========================================
   HA Prefill Infinite Scroll (V5)
   - Fills empty space by loading next pages
   - MutationObserver for CloudSearch grid replacements
   - 🔍 Dedup: skips products already in the grid
   - 📄 Page Jump: dropdown in pagination bar
   - ⏳ Loading indicator while fetching pages
   - 🛑 Next batch link after ~500 cards (fresh page load)
   - CSS in ha-infinite-scroll.css
   ========================================== */

(function () {
  // Skip search pages — use native search pagination
  if (location.pathname === "/search" || location.pathname === "/a/search") {
    console.log("🧾 HA Prefill: skipped (search page) 🔍");
    return;
  }

  const CFG = {
    gridSelector: "#product-grid",
    itemSelector: "#product-grid > li.grid__item",
    paginationWrapperSelector: ".pagination-wrapper",
    paginationLinkSelector: ".pagination__item[href*='page']",

    // How close to bottom before we consider it "needs more"
    bottomBufferPx: 3000,

    // Safety limits
    maxLoadsPerBurst: 6,
    maxTotalLoads: 13,       // ~500 cards (13 pages × ~40 per page)
    debounceMs: 200
  };

  let isLoading = false;
  let totalLoads = 0;
  let debounceTimer = 0;

  function log(...args) {
    console.log("🧾 HA Prefill:", ...args);
  }

  // ========================
  // Helpers
  // ========================

  function buildPageUrl(pageNum) {
    const url = new URL(window.location.href);
    url.searchParams.set("page", String(pageNum));
    return url.toString();
  }

  function getTotalPages() {
    const wrapper = document.querySelector(CFG.paginationWrapperSelector);
    if (!wrapper) return 1;

    let maxPage = 1;
    const links = wrapper.querySelectorAll(CFG.paginationLinkSelector);
    links.forEach(a => {
      const m = a.href.match(/page=(\d+)/);
      if (m) {
        const p = parseInt(m[1], 10);
        if (p > maxPage) maxPage = p;
      }
    });

    // Also check text content of pagination items (e.g. "105")
    wrapper.querySelectorAll(".pagination__item").forEach(el => {
      const n = parseInt(el.textContent.trim(), 10);
      if (Number.isFinite(n) && n > maxPage) maxPage = n;
    });

    return maxPage;
  }

  // ========================
  // Dedup helper
  // ========================

  function getExistingProductUrls() {
    const grid = document.querySelector(CFG.gridSelector);
    if (!grid) return new Set();
    return new Set(
      Array.from(grid.querySelectorAll('a.full-unstyled-link[href^="/products/"]'))
        .map(a => a.getAttribute("href"))
    );
  }

  // ========================
  // Loading indicator
  // ========================

  function showLoader() {
    if (document.querySelector(".ha-loading-indicator")) return;
    const grid = document.querySelector(CFG.gridSelector);
    if (!grid) return;
    const loader = document.createElement("div");
    loader.className = "ha-loading-indicator";
    loader.textContent = "⏳ Loading more models…";
    grid.parentElement.insertBefore(loader, grid.nextSibling);
  }

  function hideLoader() {
    const ldr = document.querySelector(".ha-loading-indicator");
    if (ldr) ldr.remove();
  }

  // ========================
  // 🛑 Next Batch Link
  // Shows a link to continue from the next page (fresh page load)
  // ========================

  function showNextBatchLink() {
    if (document.querySelector(".ha-next-batch")) return;

    const nextUrl = getNextPageUrl();
    if (!nextUrl) return;

    const grid = document.querySelector(CFG.gridSelector);
    if (!grid) return;

    const container = document.createElement("div");
    container.className = "ha-next-batch";

    const link = document.createElement("a");
    link.href = nextUrl;
    link.textContent = "Load next batch →";
    container.appendChild(link);

    grid.parentElement.insertBefore(container, grid.nextSibling);
    log("Next batch link shown 🛑");
  }

  // ========================
  // Pagination / next page
  // ========================

  function getNextPageUrl() {
    const wrapper = document.querySelector(CFG.paginationWrapperSelector);
    if (!wrapper) return null;

    const relNext = wrapper.querySelector('a[rel="next"]');
    if (relNext && relNext.href) return relNext.href;

    const links = Array.from(wrapper.querySelectorAll(CFG.paginationLinkSelector))
      .filter(a => a && a.href);

    if (!links.length) return null;

    const active = wrapper.querySelector('.pagination__item--current, .pagination__item[aria-current="page"]');
    if (active) {
      const currentPage = parseInt(active.textContent.trim(), 10);
      if (Number.isFinite(currentPage)) {
        const next = links.find(a => {
          const m = a.href.match(/page=(\d+)/);
          return m && parseInt(m[1], 10) === currentPage + 1;
        });
        if (next) return next.href;
      }
    }

    return links[links.length - 1].href;
  }

  function needsMoreContent() {
    const doc = document.documentElement;
    const remaining = doc.scrollHeight - (window.scrollY + window.innerHeight);
    return remaining < CFG.bottomBufferPx;
  }

  async function loadNextPageAndAppend() {
    const grid = document.querySelector(CFG.gridSelector);
    if (!grid) return false;

    const nextUrl = getNextPageUrl();
    if (!nextUrl) return false;

    isLoading = true;
    totalLoads += 1;

    // ⏳ Show loading indicator
    showLoader();

    try {
      const res = await fetch(nextUrl, { credentials: "same-origin" });
      if (!res.ok) return false;

      const html = await res.text();
      const parser = new DOMParser();
      const doc2 = parser.parseFromString(html, "text/html");

      const newItems = Array.from(doc2.querySelectorAll(CFG.itemSelector));
      if (!newItems.length) return false;

      // 🔍 Dedup: collect existing product URLs in grid
      const existingUrls = getExistingProductUrls();

      // Append only items not already in the grid
      const frag = document.createDocumentFragment();
      let skipped = 0;
      newItems.forEach(li => {
        const link = li.querySelector('a.full-unstyled-link[href^="/products/"]');
        const href = link ? link.getAttribute("href") : null;
        if (href && existingUrls.has(href)) {
          skipped++;
          return; // ⏭️ skip duplicate
        }
        frag.appendChild(li);
      });
      grid.appendChild(frag);

      if (skipped > 0) log("Skipped", skipped, "duplicates ⏭️");

      // Update pagination wrapper HTML so "next" keeps moving
      const pag1 = document.querySelector(CFG.paginationWrapperSelector);
      const pag2 = doc2.querySelector(CFG.paginationWrapperSelector);
      if (pag1 && pag2) {
        pag1.innerHTML = pag2.innerHTML;
        // Re-inject page jump widget after pagination update
        injectPageJump();
      }

      // Let any observers/lazy image loaders wake up
      window.dispatchEvent(new Event("scroll"));
      window.dispatchEvent(new Event("resize"));

      return true;
    } catch (e) {
      console.warn("🧾 HA Prefill load error:", e);
      return false;
    } finally {
      isLoading = false;
      hideLoader();
    }
  }

  async function prefillBurst(reason) {
    if (isLoading) return;
    if (!document.querySelector(CFG.gridSelector)) return;
    if (totalLoads >= CFG.maxTotalLoads) return;

    let loads = 0;

    while (needsMoreContent() && loads < CFG.maxLoadsPerBurst && totalLoads < CFG.maxTotalLoads) {
      const ok = await loadNextPageAndAppend();
      if (!ok) break;
      loads += 1;
    }

    if (loads > 0) log("Prefill done:", reason, "→", loads, "pages loaded");

    // 🛑 If we hit the session limit, show a "next batch" link
    if (totalLoads >= CFG.maxTotalLoads) {
      showNextBatchLink();
    }
  }

  function requestPrefill(reason) {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => prefillBurst(reason), CFG.debounceMs);
  }

  // ========================
  // 📄 Page Jump Widget
  // ========================

  function goToPage(pageNum) {
    const total = getTotalPages();
    pageNum = Math.max(1, Math.min(total, pageNum));
    window.location.href = buildPageUrl(pageNum);
  }

  function injectPageJump() {
    // Don't inject twice
    if (document.querySelector(".ha-page-jump")) return;

    const list = document.querySelector(".pagination__list");
    if (!list) return;

    const totalPages = getTotalPages();
    if (totalPages <= 1) return;

    // Build dropdown
    const select = document.createElement("select");
    select.className = "ha-page-select";
    select.title = "Jump to page";

    const placeholder = document.createElement("option");
    placeholder.value = "";
    placeholder.textContent = "Page…";
    placeholder.disabled = true;
    placeholder.selected = true;
    select.appendChild(placeholder);

    for (let i = 1; i <= totalPages; i++) {
      const opt = document.createElement("option");
      opt.value = String(i);
      opt.textContent = String(i);
      select.appendChild(opt);
    }

    select.addEventListener("change", () => {
      const val = parseInt(select.value, 10);
      if (Number.isFinite(val)) goToPage(val);
    });

    // Wrap in <li> and append to the pagination list
    const li = document.createElement("li");
    li.className = "ha-page-jump";
    li.appendChild(select);
    list.appendChild(li);
    // 🎲 Random page dice
    const diceLi = document.createElement("li");
    diceLi.className = "ha-page-dice";
    const diceImg = document.createElement("img");
    const diceSrc = "https://cdn.shopify.com/s/files/1/0763/6005/7098/files/icon_random_small_390657c0-26eb-4ba4-8ae9-849a11a55bc1.gif";
    const diceHoverSrc = "https://cdn.shopify.com/s/files/1/0763/6005/7098/files/diceanim.gif";
    diceImg.src = diceSrc;
    diceImg.alt = "Random page";
    diceImg.title = "Jump to a random page";
    diceLi.appendChild(diceImg);
    diceLi.addEventListener("mouseenter", () => { diceImg.src = diceHoverSrc; });
    diceLi.addEventListener("mouseleave", () => { diceImg.src = diceSrc; });
    diceLi.addEventListener("click", () => {
      const total = getTotalPages();
      const randomPage = Math.floor(Math.random() * total) + 1;
      goToPage(randomPage);
    });
    list.appendChild(diceLi);

    log("Page jump injected ✅ (", totalPages, "pages )");
  }

  // ========================
  // 🔭 Grid MutationObserver
  // ========================

  let _gridObserver = null;
  let _observerDebounce = 0;

  function observeGrid() {
    if (_gridObserver) return;

    const grid = document.querySelector(CFG.gridSelector);
    const target = grid ? grid.parentElement || grid : null;
    if (!target) return;

    _gridObserver = new MutationObserver(() => {
      clearTimeout(_observerDebounce);
      _observerDebounce = setTimeout(() => {
        requestPrefill("cloudsearch");
      }, 400);
    });

    _gridObserver.observe(target, { childList: true, subtree: true });
    log("Grid observer started 🔭");
  }

  // ========================
  // Boot
  // ========================

  function boot() {
    if (!document.querySelector(CFG.gridSelector)) return;

    // Inject page jump widget
    injectPageJump();

    // Prefill on first load
    requestPrefill("boot");

    // When slider changes: prefill
    const slider = document.querySelector("#haCardScaleSlider");
    if (slider && slider.dataset.haPrefillBound !== "1") {
      slider.dataset.haPrefillBound = "1";
      slider.addEventListener("input", () => requestPrefill("scale"));
      slider.addEventListener("change", () => requestPrefill("scale-final"));
    }

    // When filters change
    document.addEventListener("change", (e) => {
      const t = e.target;
      if (t && (t.matches(".facets__checkbox, .facets__item input, .facet-checkbox input") || t.closest(".facets"))) {
        requestPrefill("facets");
      }
    });

    // Scroll listener
    window.addEventListener("scroll", () => requestPrefill("scroll"));

    // Grid observer for CloudSearch
    observeGrid();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }
  document.addEventListener("shopify:section:load", boot);
})();